import { GridFilterItem } from '@mui/x-data-grid-pro';

import { FilterState, OptionGroup } from '../../components/molecules/FilterBar';
import { ColumnDef } from '../../components/organisms/ServiceTable';
import { columnSelectionItems } from '../../constants/service.constant';

export interface LabelValue {
  label: string;
  value: string;
}

/** Visible columns for the V4 list page. */
const V4_VISIBLE_FIELDS = [
  'serviceId',
  'serviceTypeName',
  'serviceStatusName',
  'priorityCode',
  'clientHierarchy',
  'locationName',
  'primaryAllocation',
  'reference1',
  'createdAt',
  'clientSLASummary',
  'opsSLASummary',
];

/** Default filter: exclude Complete and Cancelled statuses. */
export const DEFAULT_FILTER_ITEMS: GridFilterItem[] = [
  { field: 'serviceStatusName', operator: 'not', value: 'Complete' },
  { field: 'serviceStatusName', operator: 'not', value: 'Cancelled' },
];

/** Map columnSelectionItems to ColumnDef[] for ServiceTable. */
export const buildColumnDefs = (): ColumnDef[] => {
  const defs: ColumnDef[] = [];
  for (const group of columnSelectionItems) {
    for (const col of group.value) {
      defs.push({
        key: col.field,
        label: col.title,
        group: group.title,
        visible: V4_VISIBLE_FIELDS.includes(col.field),
      });
    }
  }
  return defs;
};

/** Convert a GridFilterModel back into a FilterState for the FilterBar display. */
export const filterModelToFilterState = (
  model: { items: GridFilterItem[] },
): FilterState => {
  const state: FilterState = {
    statuses: [],
    jobTypes: [],
    contracts: '',
    jobId: '',
    multiSearch: '',
    advancedFilters: [],
  };

  for (const item of model.items) {
    if (item.field === 'serviceStatusName' && item.operator === 'equals' && item.value) {
      state.statuses.push(String(item.value));
    } else if (item.field === 'serviceTypeName' && item.operator === 'equals' && item.value) {
      state.jobTypes.push(String(item.value));
    } else if (item.field === 'contractName' && item.value) {
      state.contracts = String(item.value);
    } else if (item.field === 'serviceId' && item.value) {
      state.jobId = String(item.value);
    } else if (item.field === 'serviceStatusName' && item.operator === 'not') {
      // Default exclusion filters — skip, don't display these
    } else if (item.value != null) {
      state.advancedFilters.push({
        field: item.field,
        dataKey: item.field,
        type: 'text',
        operator: item.operator,
        value: String(item.value),
        display: `${item.field} ${item.operator} ${item.value}`,
      });
    }
  }

  return state;
};

/** Convert FilterState from FilterBar into a GridFilterModel for the API. */
export const buildFilterModel = (
  filters: FilterState,
): { items: GridFilterItem[] } => {
  const items: GridFilterItem[] = [];

  if (filters.jobId) {
    items.push({ field: 'serviceId', operator: 'isAnyOf', value: filters.jobId });
  }

  // When statuses are selected, use those. Otherwise keep default exclusions.
  if (filters.statuses.length > 0) {
    for (const status of filters.statuses) {
      items.push({ field: 'serviceStatusName', operator: 'equals', value: status });
    }
  } else if (!filters.jobId && !filters.multiSearch) {
    items.push(...DEFAULT_FILTER_ITEMS);
  }

  for (const jobType of filters.jobTypes) {
    items.push({ field: 'serviceTypeName', operator: 'equals', value: jobType });
  }

  if (filters.contracts.trim()) {
    items.push({ field: 'contractName', operator: 'contains', value: filters.contracts.trim() });
  }

  for (const adv of filters.advancedFilters) {
    items.push({ field: adv.dataKey, operator: adv.operator, value: adv.value });
  }

  return { items };
};

/** Build status filter options from raw status data. */
export const buildStatusOptions = (
  data: Array<{ name: string; isActive: boolean }> | undefined,
): LabelValue[] => {
  if (!data) return [];
  return data
    .filter((s) => s.isActive)
    .map((s) => ({ label: s.name, value: s.name }));
};

/** Build status groups (Open / Closed) from raw status data. */
export const buildStatusGroups = (
  data: Array<{ name: string; isActive: boolean; isClosed?: boolean }> | undefined,
): OptionGroup[] => {
  if (!data) return [];
  const active = data.filter((s) => s.isActive);
  return [
    { name: 'Open', values: active.filter((s) => !s.isClosed).map((s) => s.name) },
    { name: 'Closed', values: active.filter((s) => s.isClosed).map((s) => s.name) },
  ];
};

/** Build job type filter options from raw types data. */
export const buildJobTypeOptions = (
  data: { serviceTypes?: Array<{ name: string; isActive: boolean }> } | undefined,
): LabelValue[] => {
  if (!data?.serviceTypes) return [];
  return data.serviceTypes
    .filter((t) => t.isActive)
    .map((t) => ({ label: t.name, value: t.name }));
};
